import React, { useState } from 'react';
import Button from '#lib/components/button.jsx';
import useAuth from '#lib/hooks/use-auth.jsx';
import { formToObject } from '#lib/utils/form.js';
import useValidator, { required } from '#lib/hooks/use-validator.js';
import { getPasswordValidationRules } from '#routes/auth.jsx';
import Dialog from 'lib/components/dialog';
import Input from 'lib/components/input';
import { toast } from 'lib/components/toaster';
import { useOpenClose } from '#lib/hooks/use-boolean-state.js';
import { useDeleteApiKeys, useSaveApiKeys } from '#@profile/use-profile.js';
import Icon from '#components/icon.jsx';

export function ChangePasswordDialog(props) {
    const { isOpen, onClose } = props;
    const { changePassword } = useAuth();

    function onSubmit(event) {
        event.preventDefault();
        const form = event.target;
        const { oldpassword, password } = formToObject(form);

        changePassword.execute(oldpassword, password).then(() => {
            toast.success(_t.auth.passwordReimpostata());
            onClose();
        });
    }

    function onError(event) {
        event.preventDefault();
        const form = event.target;
        const { password, confirmpassword } = formToObject(form);
        if (password !== confirmpassword) toast.error(_t.auth.mustMatchPassword());
    }

    React.useLayoutEffect(() => {
        if (changePassword.isError) toast.error(changePassword.error);
    }, [changePassword.isError, changePassword.error]);

    const formProps = useValidator({ onSubmit, onError }, { oldpassword: [required], ...getPasswordValidationRules() });

    return (
        <Dialog isOpen={isOpen} onClose={onClose}>
            <Dialog.Panel className="w-full sm:w-96">
                <Dialog.CloseButton />
                <b>{_t.auth.changePassword()}</b>
                <p className="mt-1 text-sm">{_t.auth.changePasswordDesc()}</p>

                <form
                    id="change-password-form"
                    {...formProps}
                    className="grid scroll-px-10 grid-cols-1 gap-x-4 gap-y-3 pt-5"
                >
                    <Input.Full className="w-full" type="password" name="oldpassword" label={_t.auth.oldPassword()} />
                    <Input.Full
                        customError={false}
                        className="w-full"
                        type="password"
                        name="password"
                        label={_t.auth.newPassword()}
                    />
                    <Input.Full
                        customError={false}
                        type="password"
                        className="w-full"
                        name="confirmpassword"
                        label={_t.auth.confirmNewPassword()}
                    />

                    <Dialog.Footer color="neutral">
                        <Button type="submit" look="primary" busy={changePassword.isLoading}>
                            {_t.global.confirm()}
                        </Button>
                        <Button onClick={onClose}>{_t.global.cancel()}</Button>
                    </Dialog.Footer>
                </form>
            </Dialog.Panel>
        </Dialog>
    );
}

export default function Password() {
    const changePasswordModal = useOpenClose();
    const { meta } = useAuth();
    return (
        <>
            <section>
                <h2 className="pt-2 text-2xl font-bold mb-2 text-neutral-700">{_t.profile.security()}</h2>
                <p className="mb-3">{_t.profile.securityText()}</p>
                <div className="flex items-center">
                    <p className="mr-2 font-bold">{_t.profile.changePass()}</p>
                    <div className="ml-3">
                        <Button onClick={changePasswordModal.open}>{_t.profile.changePass()}</Button>
                    </div>
                </div>
            </section>

            <ChangePasswordDialog isOpen={changePasswordModal.isOpen} onClose={changePasswordModal.close} />
            {meta?.data?.mainUser && <ApiKeysComponent />}
        </>
    );
}

function ApiKeysComponent() {
    const [apiKey, setApiKey] = useState('');
    const [apiSecret, setApiSecret] = useState('');
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [generatedKeys, setGeneratedKeys] = useState(false);
    const [apiKeyCopied, setApiKeyCopied] = useState(false);
    const [apiSecretCopied, setApiSecretCopied] = useState(false);

    const saveApiKeysMutation = useSaveApiKeys({
        onSuccess: (data) => {
            setApiKey(data.apiKey);
            setApiSecret(data.apiSecret);
            setGeneratedKeys(true);
        }
    });
    const deleteApiKeysMutation = useDeleteApiKeys({
        onSuccess: () => {
            toast.success('API keys successfully deleted!');
            setApiKey('');
            setApiSecret('');
            setGeneratedKeys(false);
            setDialogOpen(false);
        }
    });
    const generateKeys = () => {
        saveApiKeysMutation.mutate();
    };
    const deleteKeys = () => {
        deleteApiKeysMutation.mutate();
    };
    const copyToClipboard = (value, setCopiedState) => {
        navigator.clipboard.writeText(value);
        setCopiedState(true);
        setTimeout(() => {
            setCopiedState(false);
        }, 1000);
    };

    return (
        <section className="mt-4 border-t border-gray-300 w-full max-w-md">
            <h2 className="text-2xl font-bold mt-4 mb-4 text-neutral-700">{_t.apiKey.title()}</h2>
            <p className="mb-3">{_t.apiKey.text()}</p>
            <div>
                {apiKey ? (
                    <div>
                        <div className="mb-4">
                            <h2 className="text-2xl font-bold mb-4 text-center text-primary-600">
                                {_t.apiKey.apiKey()}
                            </h2>
                            <label className="block text-gray-700 text-sm font-bold mb-2">{_t.apiKey.apiKey()}:</label>
                            <div className="flex items-center">
                                <Input.Full type="text" readOnly value={apiKey} />
                            </div>
                        </div>
                        <div className="flex items-center gap-x-4">
                            <button
                                type="button"
                                onClick={() => {
                                    alert(
                                        'Are you sure you want to regenerate the keys? Once regenerated, you will lose the old ones.'
                                    );
                                    setGeneratedKeys(false);
                                    setDialogOpen(true);
                                }}
                                className="flex items-center rounded-md border border-transparent bg-blue-100 px-3 py-2 text-sm font-medium leading-4 text-blue-700 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                {_t.apiKey.regenerate()}
                            </button>
                            <button
                                type="button"
                                onClick={deleteKeys}
                                className="flex items-center rounded-md border border-transparent bg-red-100 px-3 py-2 text-sm font-medium leading-4 text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            >
                                {_t.apiKey.delete()}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <Button
                            type="button"
                            onClick={() => {
                                setGeneratedKeys(false);
                                setDialogOpen(true);
                            }}
                        >
                            {_t.apiKey.generate()}
                        </Button>
                    </div>
                )}
                {isDialogOpen && (
                    <Dialog isOpen={isDialogOpen} onClose={() => setDialogOpen(false)}>
                        <Dialog.Panel className="w-full sm:w-96">
                            <Dialog.CloseButton />
                            <b>{_t.apiKey.titleDialog()}</b>
                            <p className="mb-3 mt-1 text-sm">{_t.apiKey.textAlert()}</p>
                            <form className="mt-5">
                                <div className="mb-4">
                                    <div className="flex items-center">
                                        <Input.Full
                                            type="text"
                                            name="api-key"
                                            label={_t.apiKey.apiKey()}
                                            value={apiKey}
                                            readOnly
                                            className="w-full border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(apiKey, setApiKeyCopied)}
                                            className="ml-2 inline-flex items-center px-2 py-1 border border-gray-300 bg-gray-100 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                        >
                                            <Icon
                                                id={apiKeyCopied ? 'mini/check-circle' : 'mini/document-duplicate'}
                                                className={apiKeyCopied ? 'text-primary-600' : ''}
                                            />
                                        </button>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <div className="flex items-center">
                                        <Input.Full
                                            type="text"
                                            name="api-secret"
                                            label={_t.apiKey.apiSecret()}
                                            value={apiSecret}
                                            readOnly
                                            className="w-full border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(apiSecret, setApiSecretCopied)}
                                            className="ml-2 inline-flex items-center px-2 py-1 border border-gray-300 bg-gray-100 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                        >
                                            <Icon
                                                id={apiSecretCopied ? 'mini/check-circle' : 'mini/document-duplicate'}
                                                className={apiSecretCopied ? 'text-primary-600' : ''}
                                            />
                                        </button>
                                    </div>
                                </div>
                                <Dialog.Footer color="neutral">
                                    {!generatedKeys ? (
                                        <Button
                                            type="button"
                                            onClick={generateKeys}
                                            look="primary"
                                            busy={saveApiKeysMutation.isLoading}
                                        >
                                            {_t.apiKey.generate()}
                                        </Button>
                                    ) : null}
                                    <Button onClick={() => setDialogOpen(false)}>{_t.apiKey.close()}</Button>
                                </Dialog.Footer>
                            </form>
                        </Dialog.Panel>
                    </Dialog>
                )}
            </div>
        </section>
    );
}
